import React, { useRef, useState } from "react";
import NiceModal from "@ebay/nice-modal-react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
} from "reactstrap";
import { useTranslate } from "hooks";
import { Icon } from "components";
import { Button } from "../Button";
import { useLanguage } from "stores/globalStore";

const ALLOWED_EXTENSIONS = ["xml"];
const ALLOWED_MIME_TYPES = ["text/xml", "application/xml"];

type Props = {
  uploadTroxnetXml: (
    lang: string,
    xmlContent: string | null,
    setLoader: (value: boolean) => void,
  ) => Promise<void>;
};

export const UploadXmlModal = NiceModal.create(
  ({ uploadTroxnetXml }: Props) => {
    const modal = NiceModal.useModal();
    const translate = useTranslate();
    const [fileText, setFileText] = useState<string | null>(null);
    const [validationErr, setValidationErr] = useState<
      string | React.JSX.Element
    >("");
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileNameRef = useRef<HTMLDivElement>(null);
    const lang = useLanguage();

    const handleInputClick = () => {
      fileInputRef.current?.click();
      if (validationErr) {
        setValidationErr("");
      }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        modal.hide();
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      const isValidExtension = ALLOWED_EXTENSIONS.includes(extension ?? "");
      const isValidMime = ALLOWED_MIME_TYPES.includes(file.type);

      if (!isValidExtension || !isValidMime) {
        setValidationErr(translate("XML_UPLOAD_ERRORTEXT"));
        e.target.value = "";
        return;
      }

      if (fileNameRef.current) {
        fileNameRef.current.textContent = file.name;
      }
      const text = await file.text();
      setFileText(text);
    };

    const handleUpload = async () => {
      await uploadTroxnetXml(lang, fileText, setIsLoading);
      setFileText(null);
      modal.hide();
    };

    const cancleUpload = () => {
      if (validationErr) {
        setValidationErr("");
      }
      modal.hide();
      setFileText(null);
    };

    return (
      <Modal isOpen={modal.visible} toggle={modal.hide}>
        <ModalHeader toggle={modal.hide}>
          {translate("XML_MODAL_TITLE")}
        </ModalHeader>
        <ModalBody>
          {isLoading && (
            <div className="d-flex flex-column mb-2  gap-1">
              <div className="d-flex align-items-center gap-1">
                <Icon iconName="upload" className="text-info" />
                <div className="text-info">
                  {translate("XML_UPLOAD_PROCESS")}
                </div>
              </div>
              <Progress
                value={100}
                animated={true}
                className="bg-gray-300 rounded-3  overflow-hidden w-50"
                color="info"
                style={{ height: "0.375rem" }}
              />
            </div>
          )}
          <input
            type="file"
            accept=".xml"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="d-none"
          />
          {validationErr && <div className="text-danger">{validationErr}</div>}
          <div className="d-flex gap-2 align-items-center">
            <Button color="primary" onClick={handleInputClick}>
              {translate("XML_MODAL_BUTTON_TEXT")}
            </Button>
            <div ref={fileNameRef} className="text-gray-600">
              {translate("XML_MODAL_EMPTY_FILE_TEXT")}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex gap-2">
          <Button
            color="outline-dark"
            onClick={cancleUpload}
            disabled={isLoading}
          >
            {translate("CANCEL")}
          </Button>
          <Button
            color="secondary"
            onClick={handleUpload}
            isLoading={isLoading}
            disabled={isLoading || !fileText}
          >
            {translate("XML_UPLOAD")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  },
);
